/**
 * Created by Ning Jiang on 2021-06-18.
 */
import {WinPresentationController} from "../WinPresentationController";
import {OktoberfestGameConfig} from "../../OktoberfestGameConfig";
import {gsap, TimelineLite} from "gsap";
import {Oktoberfest} from "../../Oktoberfest";
import {GameHelper} from "../../../core/utils/GameHelper";
import {IBetLineWinData} from "../../server/data/IOktoberfestData";
import {BetWinMode} from "../../../core/config/BetWinMode";
import {OktoberfestSymbol} from "../../symbol/OktoberfestSymbol";
import {OktoberfestSymbolState} from "../../symbol/OktoberfestSymbolConfig";
import {OktoberfestGameAssets} from "../../OktoberfestGameAssets";
import {Layers, OktoberfestLayers} from "../../view/OktoberfestLayers";


export class IndiSymWinPresentation extends WinPresentationController {
    private _mainTimeline: TimelineLite | null;

    constructor() {
        super("IndiSymWinPresentation", true);
    }

    public getIndiSymWinAmtWaysTl(winAmt: number, lastWinningSym: number[]): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            win = new PIXI.BitmapText(Oktoberfest.apiPlugin.currency.formatValue(winAmt), {
                fontName: OktoberfestGameAssets.BITMAP_FONT_WIN_COUNT_UP,
                fontSize: 60,
                tint: 0xffffff
            });

        while (win.width > OktoberfestGameConfig.SYM_WIDTH) {
            win.fontSize--;
        }
        win.pivot.set(win.width / 2, win.height / 2);
        const xPos = [59, 211, 367, 519, 675];
        win.position.set(xPos[lastWinningSym[0]], 177 + lastWinningSym[1] * OktoberfestGameConfig.SYM_HEIGHT);
        tl.add(() => {
            OktoberfestLayers.getLayer(Layers.FOREGROUND)?.addChild(win);
        })
        tl.to(win, {duration: 2});
        tl.add(() => {
            OktoberfestLayers.getLayer(Layers.FOREGROUND)?.removeChild(win);
        })
        return tl;
    }

    protected hasWin(): boolean {
        return Oktoberfest.apiPlugin.gameClientConfiguration.displayIndividualWinsInGame && (Oktoberfest.model.parsedGameData.betWins?.length > 0);
    }

    protected async createWinAnimation(): Promise<any> {
        const tl: TimelineLite = gsap.timeline();

        tl.add(this.getWinAnimation());
        tl.add(() => {
            Oktoberfest.betlines.onHideAllBetLines();
        });
        const isBigWin: boolean = Oktoberfest.model.parsedGameData.currentWinRatioConfig!.bigWinLevel > -1;
        const reels: string[][] = Oktoberfest.model.parsedGameData.reels;
        const isNextModeNormal: boolean = Oktoberfest.model.parsedGameData.nextMode === "NORMAL";
        if ((GameHelper.isReel2Stacked(reels) && (GameHelper.isReel4Stacked(reels))) && (!isBigWin) && (!isNextModeNormal)) {
            tl.add(() => {
                GameHelper.resetAllSymbols();
            }, tl.totalDuration());
        }
        this._mainTimeline = tl;
        await Promise.resolve(tl);
        this._mainTimeline = null;
    }

    protected getWinAnimation(): TimelineLite {
        const winRatio: number = Oktoberfest.model.parsedGameData.currentWinRatioConfig?.ratio!;
        const tl: TimelineLite = gsap.timeline();
        (<IBetLineWinData[]>Oktoberfest.model.parsedGameData.betWins).forEach((w: IBetLineWinData, index: number) => {
            if (OktoberfestGameConfig.BET_WIN_MODE === BetWinMode.BET_LINE) {
                tl.add(() => {
                    Oktoberfest.betlines.onHideAllBetLines();
                });
                tl.add(this.getSingleBetLineWinAnimation(<IBetLineWinData>w, index, winRatio), tl.duration());
                tl.add(() => {
                    Oktoberfest.betlines.onShowWinBetLine(w.betLineIndex + 1)
                }, "<");
            }
        });
        return tl;
    }

    protected getSingleBetLineWinAnimation(winData: IBetLineWinData, index: number, winRatio: number): TimelineLite {
        const tl: TimelineLite = gsap.timeline();
        let symbolDelay: number = 0.05;
        let sym: number[] = [];
        for (let betLineIndex = 0; betLineIndex < winData.nrOfSymbols; betLineIndex++) {
            const data = this.getSymbolWinAnimation(betLineIndex, winData.betLine[betLineIndex], winData);
            tl.add(data[0].play(), symbolDelay);
            symbolDelay += symbolDelay;
            sym = [betLineIndex, winData.betLine[betLineIndex]];
        }
        tl.add(this.getIndiSymWinAmtWaysTl(winData.multipliedAmount, sym), 0);
        return tl;
    }

    protected getSymbolWinAnimation(reelId: number, symId: number, winData: IBetLineWinData): [TimelineLite, OktoberfestSymbol] {
        let sym: OktoberfestSymbol = Oktoberfest.reels[reelId].getSymbol(symId, false, false);
        const tl: TimelineLite = new TimelineLite({
            paused: true
        });

        if (sym.stackedSymbolName.isStacked) {
            if (sym.symbolName.indexOf("W") > -1) {
                let stackIndex: number = sym.stackedSymbolName.index;
                if (stackIndex !== 2) {
                    sym = Oktoberfest.reels[reelId].getSymbol(symId + (2 - stackIndex));
                }
            }
        }

        if (sym.symbolName !== "W3") {
            tl.add(() => {
                Oktoberfest.reels[reelId].reelView.addWinSymbol(sym);
            }, 0);
            tl.add(sym.getStateAnimation(OktoberfestSymbolState.WIN, {duration: 0.01, alpha: 1}, {
                duration: 0.01,
                alpha: 0
            }), 0);
        }
        if (sym.symbolName === "W3") {
            tl.add(() => {
                Oktoberfest.reels[reelId].reelView.addWinSymbol(sym);
            }, 0);
            tl.add(sym.getStateAnimation(OktoberfestSymbolState.WIN, {duration: 0.01, alpha: 1}, {
                duration: 0.01,
                alpha: 0
            }), 0);
        }
        return [tl, sym];
    }

    protected skip(): void {
        if (this._mainTimeline!.isActive()) {
            this._mainTimeline?.timeScale(5);
        }
    }
}
